import React, { useState } from 'react';
import PageHeader from '../../components/shared/PageHeader';
import { Card } from '../../components/shared/DataToolbar';
import { LoadingState, ErrorState } from '../../components/shared/Pending';
import { useApi } from '../../api/useApi';
import { stripeConnect, stripeConnectOnboarding, stripeConnectDashboard } from '../../api/hostApi';
import { Button } from '../../components/ui/button';
import { CheckCircle2, ExternalLink, RotateCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function PayoutSetup() {
  const { data, loading, error, refetch } = useApi(stripeConnect, []);
  const [busy, setBusy] = useState(false);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const res = await stripeConnectOnboarding();
      if (res?.url) window.location.href = res.url;
    } catch (e) {
      toast.error(e?.message || 'Onboarding failed');
    } finally {
      setBusy(false);
    }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const res = await stripeConnectDashboard();
      if (res?.url) window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(e?.message || 'Could not open dashboard');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const account = data?.account || null;
  const demoAccount = Boolean(data?.demo_account);
  const active = Boolean(data?.active);
  const status = demoAccount ? 'demo' : (active ? 'active' : (account?.onboarding_status || (account ? 'pending' : 'not_started')));
  const requirements = account?.requirements_currently_due || [];
  const statusMap = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    demo: 'bg-blue-50 text-blue-700 border-blue-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    restricted: 'bg-amber-50 text-amber-700 border-amber-200',
    verification_pending: 'bg-amber-50 text-amber-700 border-amber-200',
    onboarding_started: 'bg-amber-50 text-amber-700 border-amber-200',
    not_started: 'bg-slate-100 text-slate-700 border-slate-200',
  };

  return (
    <div>
      <PageHeader title="Payout setup" description="Set up your Stripe payout account so booking payments can be split and your host share routed directly to you." />
      <Card className="p-6">
        <h3 className="text-lg font-bold text-slate-900">Payout Account Status</h3>
        <p className="text-sm text-slate-500 mt-1">Properties can only be published after Stripe confirms that your payout setup is complete.</p>

        {demoAccount && (
          <div className="mt-5 rounded-lg bg-blue-50 border border-blue-200 p-4">
            <div className="flex items-center gap-2 font-semibold text-blue-900"><AlertCircle className="h-4 w-4" /> Demo payout account</div>
            <p className="mt-1 text-sm text-blue-800">This seeded account keeps demo listings usable, but Stripe cannot send real or test payouts to it. Connect a real Stripe test account before testing booking settlement.</p>
          </div>
        )}

        <div className="mt-5"><span className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-semibold ${statusMap[status] || statusMap.not_started}`}>Status: {String(status).replaceAll('_', ' ')}</span></div>
        <div className="grid md:grid-cols-3 gap-4 mt-5">
          <Flag ok={Boolean(account?.stripe_account_id)} label="Account created" />
          <Flag ok={!demoAccount && Boolean(account?.details_submitted)} label="Details submitted" />
          <Flag ok={!demoAccount && Boolean(account?.payouts_enabled)} label="Payouts enabled" />
        </div>
        {account?.stripe_account_id && <div className="mt-4 text-sm"><span className="text-slate-500">Payout Account:</span> <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono text-xs">{account.stripe_account_id}</code></div>}
        {!demoAccount && account?.last_verified_at && <div className="mt-1 text-sm"><span className="text-slate-500">Last checked:</span> {new Date(account.last_verified_at).toLocaleString()}</div>}
        {!demoAccount && requirements.length > 0 && (
          <div className="mt-5 rounded-lg bg-amber-50 border border-amber-200 p-4">
            <div className="flex items-center gap-2 font-semibold text-amber-900"><AlertCircle className="h-4 w-4" /> Information currently required by Stripe</div>
            <ul className="mt-2 ml-6 list-disc text-sm text-amber-800 space-y-0.5">{requirements.map((requirement) => <li key={requirement} className="font-mono text-xs">{requirement}</li>)}</ul>
          </div>
        )}
        <div className="mt-5 flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={refetch} disabled={busy}><RotateCw className={`h-4 w-4 mr-1.5 ${busy ? 'animate-spin' : ''}`} /> Refresh status</Button>
          {demoAccount ? (
            <Button className="bg-teal-700 hover:bg-teal-800" onClick={startOnboarding} disabled={busy}>Connect real Stripe account <ExternalLink className="h-4 w-4 ml-1.5" /></Button>
          ) : active ? (
            <Button className="bg-teal-700 hover:bg-teal-800" onClick={openDashboard} disabled={busy}>Open payout dashboard <ExternalLink className="h-4 w-4 ml-1.5" /></Button>
          ) : (
            <Button className="bg-teal-700 hover:bg-teal-800" onClick={startOnboarding} disabled={busy}>Continue Stripe onboarding <ExternalLink className="h-4 w-4 ml-1.5" /></Button>
          )}
        </div>
      </Card>

      <Card className="p-6 mt-6">
        <h3 className="text-lg font-bold text-slate-900">How booking splits work</h3>
        <p className="text-sm text-slate-600 mt-2">The guest pays the full booking amount. Stripe Connect routes 88% to the host account and returns StayEasy's 12% application fee to the platform. StayEasy pays Stripe's processing fee from its own platform balance.</p>
        <div className="mt-4 text-sm"><span className="font-semibold text-slate-900">Publish gate:</span> <span className="text-slate-700">Draft properties can be created, but publishing is blocked by the API until payout setup is active.</span></div>
      </Card>
    </div>
  );
}

function Flag({ ok, label }) {
  return <div className="rounded-lg border border-slate-200 p-4"><div className="font-semibold text-slate-900">{label}</div><div className={`mt-1 text-sm ${ok ? 'text-emerald-600' : 'text-slate-500'}`}>{ok ? <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Yes</span> : 'No'}</div></div>;
}
